import React from 'react';
import { Loader2 } from 'lucide-react';

/**
 * D5 — el botón de la aplicación.
 *
 * Las variantes son SEMÁNTICAS, no posicionales: dicen qué clase de acción es, no
 * dónde está el botón. Antes la acción principal era de cuatro colores según la
 * pantalla —azul en `FestivosModal`, verde en `AsignarClientesMasivos`, y en
 * `AgregarPlazaCliente` azul en el paso 1 y verde en el paso 2 del MISMO modal—,
 * y 41 ficheros llevaban el azul escrito a mano frente a 23 que usaban la marca.
 *
 * Tres reglas van encerradas aquí a propósito, porque son trampas ya pisadas:
 *
 *  1. **`danger` NO se tiñe con la marca.** El rojo significa "esto borra". Un
 *     centro con marca roja haría que guardar pareciera peligroso, y un "Eliminar"
 *     azul marino pierde la señal de alto.
 *  2. **`neutral` NO es `bg-brand-secondary`.** Ese token lo fija el centro y por
 *     defecto es BLANCO: es lo que dejó seis botones "Cancelar" invisibles.
 *  3. **Sobre el primario no va `text-white` a secas**, sino
 *     `text-brand-primary-foreground`, que BrandingContext calcula por luminancia.
 *     Con un primario claro, el blanco desaparece.
 */
export type ButtonVariant = 'primary' | 'neutral' | 'danger' | 'ghost' | 'secondary';

const VARIANTS: Record<ButtonVariant, string> = {
    primary: 'bg-brand-primary text-brand-primary-foreground hover:brightness-90 border border-transparent',
    neutral: 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50',
    danger: 'bg-red-600 text-white hover:bg-red-700 border border-transparent',
    ghost: 'bg-transparent text-gray-600 hover:bg-gray-100 border border-transparent',
    // Alias de `neutral`. Este componente ya existía con `primary | secondary` y lo
    // usa la consola de superadmin; se mantiene para no romperla. En código nuevo,
    // `neutral`: dice lo que es (una acción secundaria) y no dónde está.
    secondary: 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50',
};

const SIZES = {
    sm: 'px-2.5 py-1.5 text-xs gap-1',
    md: 'px-4 py-2 text-sm gap-1.5',
} as const;

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant;
    size?: keyof typeof SIZES;
    /** Muestra un spinner y bloquea el botón. */
    loading?: boolean;
    /** Ocupa todo el ancho: útil en móvil y dentro de modales estrechos. */
    block?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
    variant = 'primary',
    size = 'md',
    loading = false,
    block = false,
    disabled,
    className = '',
    children,
    ...props
}) => (
    <button
        // `type` por defecto a "button": dentro de un <form>, el defecto del
        // navegador es "submit", y ese es el fallo clásico de un botón que envía
        // el formulario sin querer.
        type={props.type ?? 'button'}
        disabled={disabled || loading}
        className={[
            'inline-flex items-center justify-center rounded-md font-medium transition-colors',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            VARIANTS[variant],
            SIZES[size],
            block ? 'w-full' : '',
            className,
        ].filter(Boolean).join(' ')}
        {...props}
    >
        {loading && <Loader2 className="animate-spin" size={size === 'sm' ? 12 : 16} />}
        {children}
    </button>
);

export default Button;
